/**
 * Thin fetch wrapper for the hub API. Every write carries the x-otii-csrf
 * header the server requires; a 401 anywhere sends the app to sign-in.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details: { code?: string; fields?: Record<string, string>; [k: string]: unknown } | null,
  ) {
    super(message);
  }
  /** Field-level validation messages from the server, if any. */
  get fields(): Record<string, string> {
    return this.details?.fields ?? {};
  }
  get code(): string | undefined {
    return this.details?.code;
  }
}

export const UNAUTHORISED_EVENT = "otii:unauthorised";

export async function api<T = unknown>(path: string, init: { method?: string; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  const method = init.method ?? (init.body === undefined ? "GET" : "POST");
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    signal: init.signal,
    headers: {
      accept: "application/json",
      ...(init.body !== undefined ? { "content-type": "application/json" } : {}),
      ...(method !== "GET" ? { "x-otii-csrf": "1" } : {}),
    },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith("/api/auth/")) window.dispatchEvent(new Event(UNAUTHORISED_EVENT));
    const err = data as { error?: string; details?: ApiError["details"] } | null;
    throw new ApiError(res.status, err?.error ?? `Request failed (${res.status}).`, err?.details ?? null);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

export const get = <T>(path: string, signal?: AbortSignal) => api<T>(path, { signal });
export const post = <T = { ok: true }>(path: string, body: unknown = {}) => api<T>(path, { method: "POST", body });
export const patch = <T = { ok: true }>(path: string, body: unknown) => api<T>(path, { method: "PATCH", body });
export const put = <T = { ok: true }>(path: string, body: unknown) => api<T>(path, { method: "PUT", body });
export const del = <T = { ok: true }>(path: string) => api<T>(path, { method: "DELETE" });

/** Build a query string from an object, dropping empty values and joining arrays with commas. */
export function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      if (v.length) u.set(k, v.join(","));
    } else u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}
